import { useMemo, useState } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Popover from "react-bootstrap/Popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS } from "@constants/policy-config-decision-component-result-options";
import { LayoutTypes } from "@enums/layout-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { useOutsideClick } from "@hooks/use-outside-click";
import { SelectOption } from "@idw-types/common";
import {
    DeployedPolicyComponent,
    SimpleDeployedPolicyComponentCondition
} from "@idw-types/data/reference-data/generic";
import {
    FormattedRpServiceConfigurationPolicyConfigDecisionCondition,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { AxnAttributeTypeField } from "@idw-types/data/reference-data/axn";
import { getPolicyDecisionResult } from "@utils/policies/policy-util";
import { PolicyDecisionConditionResultDiagram } from "@containers/reports/verification-transaction-details/policy-decisions-table-container/policy-decision-condition-result-diagram/policy-decision-condition-result-diagram";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { LabelValueContainer } from "@containers/common/label-value-container/label-value-container";
import styles from "./policy-decision-condition-component-container.less";

type PolicyDecisionConditionComponentProps = {
    isDisabled: boolean;
    condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition;
    deployedPolicyComponents: Array<DeployedPolicyComponent>;
    deployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    updateCondition: (condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => void;
    removeCondition: (condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => void;
};

export const PolicyDecisionConditionComponentContainer = (props: PolicyDecisionConditionComponentProps) => {
    const {
        isDisabled,
        condition,
        deployedPolicyComponents,
        deployedPolicyComponentConditions,
        axnAttributeTypeFields,
        serviceOfferings,
        updateCondition,
        removeCondition
    } = props;
    
    const [isDiagramOpen, setIsDiagramPopoverOpen] = useState(false);

    const handleResultChange = (newValue: SelectOption) => {
        updateCondition({
            ...condition,
            values: newValue.value
        });
    };

    const handleRemoveCondition = () => {
        removeCondition(condition);
    };

    const renderTooltip = (props: any) => {
        return (
            <Tooltip {...props}>{condition.component.description}</Tooltip>
        );
    };


    const policyDecisionConditionResult = useMemo(() => {
        return getPolicyDecisionResult(
            condition.component,
            deployedPolicyComponents,
            deployedPolicyComponentConditions,
            serviceOfferings,
            axnAttributeTypeFields
        );
    }, [
        condition,
        deployedPolicyComponents,
        deployedPolicyComponentConditions,
        serviceOfferings,
        axnAttributeTypeFields
    ]);

    const handleClosePolicyDiagram = () => {
        setIsDiagramPopoverOpen(false);
    }

    const ref = useOutsideClick(handleClosePolicyDiagram);
    const handleDiagramPopoverClick = () => {
        setIsDiagramPopoverOpen(!isDiagramOpen);
    }

    const renderPolicyComponentDiagram = (props: any) => {
        return (<Popover className={styles.policyVisualizationPopover} id={"policy-decision-condition-visualization-popover"} {...props} >
            <Popover.Header className={styles.policyVisualizationPopoverHeader}>
                <FontAwesomeIcon onClick={handleClosePolicyDiagram} className={styles.closeButton} icon={faTimesCircle} />
            </Popover.Header>
            <Popover.Body className={styles.policyVisualizationPopoverBody}>
                <PolicyDecisionConditionResultDiagram
                    policyDecisionConditionResult={policyDecisionConditionResult[0]}
                    depth={0}
                    index={0}
                    siblingCount={0}
                    parentPolicyDecisionConditionResult={null}
                    andOrTextBackgroundColor={"#fff"}
                />
            </Popover.Body>
        </Popover>)
    }


    const selectedOption = useMemo(() => {
        const firstChoice = POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS.find(option => option.value === condition.values);
        if (firstChoice) {
            return firstChoice
        } else if (condition.values === "0") {
            return POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS[1];
        } else if (condition.values === "1") {
            return POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS[0];
        }
        return null;
    }, [condition.values]);

    return (
        <div className={styles.policyDecisionConditionComponentContainer}>
            <LabelValueContainer
                controlId={`policy-decision-condition-component-${condition.component.id}-label-value`}
                className={styles.nameLabel}
                isRequired={false}
                label={condition.component.name}
                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                labelIsBold={false}
                layout={LayoutTypes.ROW}
                value={" "}
                afterValueIcon={faQuestionCircle}
            />
            <OverlayTrigger overlay={renderTooltip}>
                <div className={styles.tooltipIconContainer}><FontAwesomeIcon className={styles.tooltipIcon} icon={faQuestionCircle} /></div>
            </OverlayTrigger>


            <OverlayTrigger show={isDiagramOpen} trigger="click" placement="auto" overlay={renderPolicyComponentDiagram}>
                <div ref={ref} onClick={handleDiagramPopoverClick} className={styles.tooltipPolicyDiagramIconContainer}>
                    <FontAwesomeIcon className={styles.tooltipIcon} icon={faEye} />
                </div>
            </OverlayTrigger>

            <SelectFormGroup
                controlId={`policy-decision-condition-component-${condition.component.id}-result-select`}
                automationId={`policy-decision-condition-component-${condition.component.id}-result-select`}
                className={styles.resultSelect}
                ariaLabel={"is"}
                label={"is"}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                onChange={handleResultChange}
                options={POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS}
                selectedOption={selectedOption}
            />
            {!isDisabled && (
                <span id={`policy-decision-condition-component-${condition.component.id}-remove-condition`} className={styles.removeIconContainer} onClick={handleRemoveCondition}>
                    <FontAwesomeIcon className={styles.removeIcon} icon={faTimesCircle} />
                </span>
            )}
        </div>
    );
};